import { useMemo, type ReactNode } from "react";
import { StyleSheet, Text } from "react-native";

import { DisclosureCard } from "../primitives/DisclosureCard";
import { useDisclosure } from "../primitives/useDisclosure";
import type { UiTheme } from "../theme";
import { panelTitleTextStyle } from "./InfoPill";
import { WidgetCollapseContext } from "./WidgetCollapseButton";

// PG-57: a collapsed Progress widget. Closed, the DisclosureCard header is its
// summary, the name in the panels' own title voice and a chevron; the panel is
// not mounted, so it fetches nothing. Open, the card gives way to the panel
// itself, with its own title and frame, and the ▲ that closes it sits at the
// end of that title row (WidgetCollapseButton, through the context below).
export function ProgressCollapsibleWidget({
  theme,
  label,
  children,
}: {
  theme: UiTheme;
  label: string;
  children: ReactNode;
}) {
  const styles = makeStyles(theme);
  const { expanded, toggle } = useDisclosure(false);
  const collapse = useMemo(() => ({ label, onCollapse: toggle }), [label, toggle]);
  if (!expanded) {
    return (
      <DisclosureCard
        theme={theme}
        expanded={false}
        onToggle={toggle}
        accessibilityLabel={label}
        header={<Text style={styles.title}>{label}</Text>}
      >
        {null}
      </DisclosureCard>
    );
  }
  return (
    <WidgetCollapseContext.Provider value={collapse}>
      {children}
    </WidgetCollapseContext.Provider>
  );
}

function makeStyles(theme: UiTheme) {
  return StyleSheet.create({ title: panelTitleTextStyle(theme) });
}
